/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, DownloadService, HttpService } from '@dm/core';
import { ToolService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';
import { environment } from 'environments/environment';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type CheckDetail, CheckResult, UPGRADE_QUERY_TYPE } from './upgrade.const';

@Injectable()
export class UpgradeService {
    private preCheckStatus = 'null';

    constructor(
        private toolService: ToolService,
        private httpService: HttpService,
        private downloadService: DownloadService,
    ) { }

    getPrechekStatus(): string {
        return this.preCheckStatus;
    }

    setPrecheckStatus(val: string): void {
        this.preCheckStatus = val;
    }

    /**
     * 判断是否所有检查项已完成
     * items 检查项数组
     * doneKey  检查项对象状态字段key值
     * doneStatus 所有能表示完成状态的取值
     */
    ifAllDone(items: any[], doneKey: string, doneStatus: number[]): boolean {
        return !items.some((item) => !doneStatus.includes(Number(item[doneKey])));
    }

    /**
     * 是否展示快速升级\ota升级特性，高端环境且为release包不展示快速升级、ota升级特性
     * 返回undefined表明还未获取到设备信息，无法判断
     */
    ifShowUpgrade = (): boolean => {
        const isProdHigh = FeatureConfig.IsNotSupportFastUpgrade;
        if (isProdHigh === undefined) {
            return undefined;
        }
        return !isProdHigh || (environment.env !== 'release' && environment.env !== 'release_crack');
    };

    /**
     * 查询获取导出配置数据路径
     */
    getDataBaseInportPath(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.exportPath');
        return this.httpService.get(rest, options);
    }

    /**
     * 下载配置数据文件
     */
    backupActivatedDataBase(path: string, time: string): Observable<boolean> {
        const rest = this.httpService.makeUrl('upgrade.fileConfig')
            .setParams({ path });
        return this.downloadService.download(rest, 'DataBaseFile' + time + '.dat');
    }

    /**
     * 获取包分发进度详情
     */
    getDistributeDetail(): Observable<any> {
        const rest = this.httpService.makeUrl('upgrade.distributeDetail').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        return this.httpService.get(rest)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 分发OTA升级包
     */
    distributeOtaPkg(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        // '/OSM/coffer_data/update/OTA'是固定的分发OTA升级包路径参数
        const rest = this.httpService.makeUrl('upgrade.distributeOtaPkg').setParams({ uploadPath: '/OSM/coffer_data/update/OTA' });
        return this.httpService.get(rest, options);
    }

    /**
     * 详情项重新排序，正在检查项和失败项放到最前面,失败项和忽略项相对位置不变
     */
    checkDetailsSort(checkDetails: CheckDetail[]): CheckDetail[] {
        const result = [...checkDetails];
        const sortLevel = {
            [CheckResult.Checking]: 0,
            [CheckResult.Error]: 1,
            [CheckResult.Fail]: 2,
            [CheckResult.Ignore]: 2,
            [CheckResult.Suc]: 3,
            [CheckResult.Uncheck]: 4,
        };
        result.sort((first, second) => {
            const firstCheckResult = sortLevel[first.checkResult];
            const secondCheckResult = sortLevel[second.checkResult];
            // 有未知项顺序不变
            if (firstCheckResult === undefined || secondCheckResult === undefined) {
                return 0;
            }
            return firstCheckResult - secondCheckResult;
        });
        return result;
    }

    /**
     * 获取检查详情数据
     *
     * @param checkItem 检查项字符串
     *
     * @param checkDetails 全部检查详情数据
     */
    getCheckDetailData(checkItem: string, checkDetails: CheckDetail[]): CheckDetail {
        return checkDetails.find((item) => item.checkItem === checkItem);
    }
}
